/**
 * Testimonials Page
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ChevronDown, ArrowRight, Star, TrendingUp, DollarSign, Clock, CheckCircle, Building2 } from "lucide-react";

const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663400393649/gEFtNmSeGBDwQH79ZrNHXT/testimonials_hero-5tYumRFgqYF7HtXmJkt9ph.webp";

const CASE_STUDIES = [
  {
    id: 1, name: "Sarah Chen", title: "Founder & CEO", company: "TechFlow Solutions", industry: "Technology",
    challenge: "Sarah needed $250K to expand her software development team.",
    solution: "SBA 7(a) Loan",
    results: { fundingAmount: "$250,000", timeToFunding: "45 days", revenueGrowth: "+180% YoY" },
    quote: "Legacy Triangle understood our vision and found the perfect funding solution.",
    rating: 5,
  },
  {
    id: 2, name: "Marcus Johnson", title: "Owner", company: "Johnson's Auto Repair", industry: "Automotive",
    challenge: "Marcus wanted to purchase $180K in diagnostic equipment.",
    solution: "Equipment Financing",
    results: { fundingAmount: "$180,000", timeToFunding: "3 days", revenueGrowth: "+35%" },
    quote: "The speed was incredible. I got approved and funded in 3 days.",
    rating: 5,
  },
  {
    id: 3, name: "Priya Patel", title: "Owner", company: "Patel's Boutique Catering", industry: "Food & Beverage",
    challenge: "Priya's catering business was booked solid but cash-strapped.",
    solution: "Working Capital Line of Credit",
    results: { fundingAmount: "$75,000", timeToFunding: "48 hours", revenueGrowth: "+40%" },
    quote: "The working capital line was a game-changer for my business.",
    rating: 5,
  },
];

const INDUSTRIES = ["All", "Technology", "Automotive", "Food & Beverage", "Construction", "Wellness"];

export default function Testimonials() {
  const [selectedIndustry, setSelectedIndustry] = useState("All");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered = selectedIndustry === "All" ? CASE_STUDIES : CASE_STUDIES.filter((cs) => cs.industry === selectedIndustry);

  return (
    <div className="min-h-screen bg-white" data-testid="testimonials-page">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden pt-32">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2d52]/95 via-[#0f2d52]/80 to-[#0f2d52]/30" />
        <div className="container relative z-10 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-0.5 w-8 bg-[#48d480]" />
              <span className="section-label">Success Stories</span>
            </div>
            <h1 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-4xl md:text-6xl font-black text-white leading-tight mb-5">
              Real Businesses, <span className="text-[#48d480]">Real Results</span>
            </h1>
            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/70 text-lg leading-relaxed mb-8 max-w-xl">
              See how businesses like yours secured funding and achieved their growth goals.
            </p>
            <Link to="/contact">
              <button className="btn-primary flex items-center gap-2 text-sm">
                Start Your Journey <ArrowRight size={15} />
              </button>
            </Link>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 max-w-3xl">
              {[
                { icon: Building2, value: "10K+", label: "Businesses Funded" },
                { icon: DollarSign, value: "$50M+", label: "Capital Distributed" },
                { icon: TrendingUp, value: "180%", label: "Avg Revenue Growth" },
                { icon: Star, value: "300+", label: "5-Star Reviews" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <stat.icon size={18} className="text-[#48d480] mb-2" />
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-white font-black text-xl">{stat.value}</div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/50 text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-10 bg-gray-50 border-b border-gray-100">
        <div className="container">
          <div className="flex flex-wrap justify-center gap-3">
            {INDUSTRIES.map((industry) => (
              <button
                key={industry}
                onClick={() => setSelectedIndustry(industry)}
                className={`px-5 py-2.5 rounded-full font-bold text-sm transition-all border-2 ${
                  selectedIndustry === industry
                    ? "bg-[#0f2d52] text-white border-[#0f2d52]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-[#0f2d52]"
                }`}
              >
                {industry}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="space-y-4">
            {filtered.map((cs) => (
              <div key={cs.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all">
                <button onClick={() => setExpandedId(expandedId === cs.id ? null : cs.id)} className="w-full">
                  <div className="flex items-start justify-between p-6 lg:p-8 text-left">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-black text-lg">{cs.name}</h3>
                        <div className="flex gap-0.5">
                          {Array(cs.rating).fill(0).map((_, i) => (
                            <Star key={i} size={13} className="fill-[#e8821c] text-[#e8821c]" />
                          ))}
                        </div>
                      </div>
                      <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-xs mb-2">{cs.title} at {cs.company}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-block bg-[#48d480]/10 text-[#48d480] text-xs font-bold px-3 py-1 rounded-full">{cs.industry}</span>
                        <span className="inline-block bg-[#e8821c]/10 text-[#e8821c] text-xs font-bold px-3 py-1 rounded-full">{cs.solution}</span>
                      </div>
                    </div>
                    <ChevronDown size={20} className={`text-[#48d480] flex-shrink-0 ml-4 transition-transform duration-300 ${expandedId === cs.id ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {expandedId === cs.id && (
                  <div className="border-t border-gray-100 px-6 lg:px-8 py-8 bg-gray-50/30">
                    <div className="grid lg:grid-cols-2 gap-8">
                      <div>
                        <h4 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-black text-sm mb-2 uppercase tracking-wider">The Challenge</h4>
                        <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 text-sm leading-relaxed mb-4">{cs.challenge}</p>
                        <div className="bg-white rounded-xl p-4 border border-[#48d480]/20">
                          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-700 text-sm italic leading-relaxed">"{cs.quote}"</p>
                        </div>
                      </div>
                      <div>
                        <h4 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-black text-sm mb-4 uppercase tracking-wider">The Results</h4>
                        <div className="space-y-3">
                          {Object.entries(cs.results).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-3 bg-white rounded-xl p-3.5 border border-gray-100">
                              <CheckCircle size={16} className="text-[#48d480] flex-shrink-0" />
                              <div>
                                <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-400 text-xs">{key.replace(/([A-Z])/g, " $1").trim()}</div>
                                <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-black text-sm">{value}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <Link to="/contact">
                          <button className="w-full mt-6 bg-[#48d480] text-[#0f2d52] font-black py-3 rounded-xl hover:bg-[#2fb866] transition-all flex items-center justify-center gap-2 text-sm">
                            Get Similar Results <ArrowRight size={14} />
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0f2d52]">
        <div className="container text-center max-w-2xl mx-auto">
          <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
            Your Business Could Be <span className="text-[#48d480]">Next</span>
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-base mb-8">
            Join thousands of businesses that have achieved their growth goals with Legacy Triangle.
          </p>
          <Link to="/contact">
            <button className="btn-primary flex items-center gap-2 mx-auto">
              Start Your Journey <ArrowRight size={16} />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
